import { Component, DestroyRef, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { FullExportsConfirmationModalComponent } from 'exports/components/full-exports-confirmation-modal/full-exports-confirmation-modal.component';
import { ExportChannel } from 'exports/interfaces/export-channels.interface';
import { ExportType } from 'exports/interfaces/export-types.interface';
import { ExportTypesFilterPipe } from 'exports/pipes/export-types-filter.pipe';
import { ExportsService } from 'exports/services/exports.service';
import { BulkOperationType } from 'listings/enums/bulk-operation-type.enum';
import { filter, switchMap } from 'rxjs';
import { ChannelsService } from 'settings/services/channels.service';
import { FormErrorComponent } from 'shared/components/form-error/form-error.component';
import { ModalService, ToastService } from 'shared/services';

@Component({
    selector: 'app-full-exports',
    templateUrl: './full-exports.component.html',
    styleUrl: './full-exports.component.scss',
    imports: [TranslateModule, FormErrorComponent, ReactiveFormsModule, ExportTypesFilterPipe],
})
export class FullExportsComponent {
    public form = new FormGroup<{
        channelId: FormControl<string>;
        exportType: FormControl<string>;
        listingIds: FormControl<string>;
    }>({
        channelId: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        exportType: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        listingIds: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    });

    public channels: Signal<ExportChannel[]> = toSignal(
        this.channelService.getChannelsForFullExport(true).pipe(takeUntilDestroyed(this.destroyRef)),
        { initialValue: [] },
    );
    public exportTypes: ExportType[] = [
        {
            label: 'exports.full-exports.selectFullExportType.sendAllFromSCHWithoutOverride.label',
            value: BulkOperationType.FULL_EXPORT_LISTINGS_WITHOUT_OVERRIDE_CHANNEL,
        },
        {
            label: 'exports.full-exports.selectFullExportType.sendAllFromSCHWithOverride.label',
            value: BulkOperationType.FULL_EXPORT_LISTING_WITH_OVERRIDE_CHANNEL,
        },
    ];

    constructor(
        public destroyRef: DestroyRef,
        public channelService: ChannelsService,
        public modalService: ModalService,
        public exportsService: ExportsService,
        public toastService: ToastService,
    ) {}

    public runFullExport() {
        this.form.updateValueAndValidity();
        this.form.markAllAsTouched();

        if (this.form.invalid) {
            return;
        }

        const listingIds = this.form.value.listingIds
            ?.split(/[,\n\r\s]+/)
            .map((id) => id.trim())
            .filter((id) => id.length > 0);

        this.modalService
            .openModal(FullExportsConfirmationModalComponent, {
                type: 'center',
                exportTypes: this.exportTypes,
                data: {
                    ...this.form.value,
                    channelId: this.channels().find((channel) => channel.value === this.form.value.channelId)?.label,
                    exportType: this.exportTypes
                        .find((exportType) => exportType.value === Number(this.form.value.exportType))
                        ?.label.replace(/\.label$/, ''),
                    listingMappingIds: listingIds,
                },
            })
            .onClose.pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(Boolean),
                switchMap(() =>
                    this.exportsService.runFullExport(Number(this.form.value.exportType) as BulkOperationType, {
                        channelId: this.form.value.channelId,
                        listingIds: listingIds,
                    }),
                ),
            )
            .subscribe(() => {
                this.form.reset();
                this.toastService.showToast('exports.full-exports.fullExportSuccess', 'success');
            });
    }
}
